import React from 'react';
import styled from 'styled-components';

import { ReportGeneratedBlock } from '../../types';
import { ResponseActions, ResponseIcon } from '../ResponseActionButton';
import { useResponseAction } from '../responseActions';
import { ResponseReply } from '../responsePrimitives';
import { responseTone, responseType, rsColor, rsv } from '../responseScale';

// 6424:11908: the tone's ink as the rule, its 14% as the fill.
const Banner = styled.section<{ $ink: string; $fill: string }>`
  display: flex;
  align-items: flex-start;
  padding: ${rsv.space('banner')};
  border: 1px solid ${({ $ink }) => $ink};
  border-radius: ${rsv.radius('panel')}px;
  background: ${({ $fill }) => $fill};
`;

// 6424:11909: a dark check on the tone.
const Tick = styled.span<{ $ink: string }>`
  display: inline-flex;
  flex: 0 0 auto;
  align-items: center;
  justify-content: center;
  width: ${rsv.size('bannerTick')}px;
  height: ${rsv.size('bannerTick')}px;
  border-radius: 50%;
  background: ${({ $ink }) => $ink};
  color: ${rsColor.page};

  .anticon {
    font-size: ${rsv.size('bannerTickGlyph')}px;
  }
`;

const Content = styled.div`
  flex: 1 1 auto;
  min-width: 0;
  margin-left: ${rsv.space('bannerGap')}px;
`;

const Title = styled.h4<{ $ink: string }>`
  margin: 0;
  color: ${({ $ink }) => $ink};
  ${responseType('bannerTitle')}
`;

const Body = styled.p`
  margin: ${rsv.space('bannerTitleToBody')}px 0 0;
  color: ${rsColor.secondary};
  white-space: pre-line;
  ${responseType('answerBody')}
`;

const Checks = styled.ul<{ $ink: string }>`
  margin: ${rsv.space('bannerChecksTop')}px 0 0;
  padding: 0;
  list-style: none;

  > li {
    display: flex;
    align-items: center;
    color: ${rsColor.secondary};
    ${responseType('check')}
  }

  > li + li {
    margin-top: ${rsv.space('bannerCheckGap')}px;
  }

  .anticon {
    margin-right: ${rsv.space('bannerCheckIconGap')}px;
    color: ${({ $ink }) => $ink};
    font-size: ${rsv.size('checkIcon')}px;
  }
`;

const Actions = styled(ResponseActions)`
  margin-top: ${rsv.actionsTop('bannerActionsTop')}px;
`;

/** Figma 6424:11520, turn 2: the report-generated banner (6424:11908). */
const ReportGenerated: React.FC<{ block: ReportGeneratedBlock }> = ({ block }) => {
  const emit = useResponseAction(block);
  const tone = responseTone(block.tone);
  return (
    <ResponseReply data-testid="orbit-response-report_generated" data-block-id={block.id}>
      <Banner $ink={tone.ink} $fill={tone.fill}>
        <Tick $ink={tone.ink}>
          <ResponseIcon icon="check" />
        </Tick>
        <Content>
          <Title $ink={tone.ink}>{block.title}</Title>
          {block.body ? <Body>{block.body}</Body> : null}
          {block.checks.length > 0 ? (
            <Checks $ink={tone.ink}>
              {block.checks.map((check, index) => (
                // Checks carry no id; their position in the list is their identity.
                // eslint-disable-next-line react/no-array-index-key
                <li key={index}>
                  <ResponseIcon icon="check" />
                  <span>{check}</span>
                </li>
              ))}
            </Checks>
          ) : null}
          <Actions actions={block.actions} onAction={emit} />
        </Content>
      </Banner>
    </ResponseReply>
  );
};

export default ReportGenerated;
